import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { useContainer } from "@contexts/container-context";
import { useCurrentUser } from "@queries/account";

import {
  POLL_INTERVAL_MS,
  POLL_TIMEOUT_MS,
  SESSION_STATUS,
} from "@views/classrooms/details/components/ai-curriculum/ai-curriculum.constants";

/**
 * Hook to check whether the AI Curriculum Assistant feature flag is enabled
 * for the current user's organization. Cache is scoped to `organizationCode`
 * so a session that switches org context doesn't serve a stale value.
 */
export const useAiCurriculumEnabled = (options = {}) => {
  const { aiCurriculumService } = useContainer();
  const currentUser = useCurrentUser();
  const orgCode = currentUser?.organizationCode;

  return useQuery({
    queryKey: ["aiCurriculumEnabled", orgCode],
    queryFn: () => aiCurriculumService.getFeatureEnabled(),
    enabled: !!orgCode,
    ...options,
  });
};

/**
 * Fetches the tunable settings catalog for the overrides panel. Disabled until
 * `enabled` (the settingsEditingEnabled flag) is true — the endpoint is
 * flag-gated server-side.
 */
export const useAiCurriculumSettings = (enabled, options = {}) => {
  const { aiCurriculumService } = useContainer();

  return useQuery({
    // Global tunables catalog — not org-specific, so the key is intentionally unscoped.
    queryKey: ["aiCurriculumSettings"],
    queryFn: () => aiCurriculumService.getSettings(),
    enabled: !!enabled,
    ...options,
  });
};

/**
 * Fetches the catalog of US states available in the state-standards corpus.
 * Returns the raw service response — caller maps to dropdown options.
 */
export const useAiCurriculumStates = (options = {}) => {
  const { aiCurriculumService } = useContainer();

  return useQuery({
    queryKey: ["aiCurriculumStates"],
    queryFn: () => aiCurriculumService.getStates(),
    ...options,
  });
};

/**
 * Fetches the top-level subjects available for a given state (e.g. "Career
 * and Technical Education", "English Language Arts"). Disabled until the
 * caller passes a non-null `state`.
 */
export const useAiCurriculumSubjects = (state, options = {}) => {
  const { aiCurriculumService } = useContainer();

  return useQuery({
    queryKey: ["aiCurriculumSubjects", state],
    queryFn: () => aiCurriculumService.getSubjects(state),
    enabled: !!state,
    ...options,
  });
};

/**
 * Fetches the source-name (framework) drill-downs available under a given
 * (state, subject) pair. Disabled until the caller passes both.
 */
export const useAiCurriculumSourceNames = (state, subject, options = {}) => {
  const { aiCurriculumService } = useContainer();

  return useQuery({
    queryKey: ["aiCurriculumSourceNames", state, subject],
    queryFn: () => aiCurriculumService.getSourceNames(state, subject),
    enabled: !!state && !!subject,
    ...options,
  });
};

/**
 * Polls GET /sessions/{id}/status every POLL_INTERVAL_MS until the session
 * reaches `targetStatus` (caller-supplied) or FAILED. Lower-level than
 * useAiCurriculumPolling; the orchestrated hook below is what most callers
 * want.
 *
 * Why a target rather than a generic "terminal" set: the backend's submit
 * endpoints (POST /standards, POST /criteria) return before flipping status
 * to the next in-flight phase — a worker does that asynchronously. If we
 * stopped on any terminal status we'd see the previous phase's terminal
 * (e.g., `standards_loaded` during the recommending phase) and advance the
 * wizard before the new work even started.
 *
 * `options.runKey` distinguishes polling episodes for the SAME (session,
 * target) pair — re-runs via back-navigation (ENG-23395). Without it, the
 * re-run's query hits the previous run's cached terminal status and the
 * wizard advances instantly instead of waiting for the new run.
 */
export const useAiCurriculumSessionStatus = (
  orgCode,
  sessionId,
  targetStatus,
  options = {}
) => {
  const { aiCurriculumService } = useContainer();
  const { runKey = 0, ...queryOptions } = options;

  return useQuery({
    queryKey: [
      "aiCurriculumSessionStatus",
      orgCode,
      sessionId,
      targetStatus,
      runKey,
    ],
    queryFn: () => aiCurriculumService.getSessionStatus(orgCode, sessionId),
    enabled: !!orgCode && !!sessionId && !!targetStatus,
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      if (!status) return POLL_INTERVAL_MS;
      if (status === SESSION_STATUS.FAILED || status === targetStatus) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: false,
    ...queryOptions,
  });
};

/**
 * One-shot session read that resolves where a refresh-restored wizard resumes
 * (ENG-23196). No polling — the loading step it lands on takes that over.
 * `gcTime: 0` so a later rehydrate re-reads rather than resuming against a
 * session that has since moved on.
 */
export const useAiCurriculumSession = (orgCode, sessionId, options = {}) => {
  const { aiCurriculumService } = useContainer();

  return useQuery({
    queryKey: ["aiCurriculumSession", orgCode, sessionId],
    queryFn: () => aiCurriculumService.getSession(orgCode, sessionId),
    enabled: !!orgCode && !!sessionId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });
};

/**
 * Higher-level polling orchestration. Fires `onComplete(response)` when the
 * session reaches `targetStatus`, `onError(message, response?)` on a FAILED
 * status or the POLL_TIMEOUT_MS hard timeout — the response carries
 * `failedStage` so callers can route to the right recovery path;
 * it is undefined on timeout. Each session only fires one terminal callback.
 *
 * Callers pass the status they're waiting for per phase — see SESSION_STATUS
 * (e.g., STANDARDS_LOADED, RECOMMENDED, BUILT, ANALYZED).
 */
export const useAiCurriculumPolling = (
  orgCode,
  sessionId,
  { targetStatus, runKey, onComplete, onError } = {}
) => {
  const completedRef = useRef(false);
  // Must be a plain boolean. React Query v5 treats a function value here as a
  // dynamic-enabled callback and will invoke it repeatedly with a Query arg —
  // which silently re-fires `onComplete` if we pass the raw truthy chain.
  const pollingEnabled = Boolean(
    orgCode && sessionId && targetStatus && onComplete
  );

  const { data } = useAiCurriculumSessionStatus(
    orgCode,
    sessionId,
    targetStatus,
    {
      enabled: pollingEnabled,
      runKey,
    }
  );

  // Surface terminal status (success or failure) to the caller.
  useEffect(() => {
    if (!data || completedRef.current) return;
    if (data.status === SESSION_STATUS.FAILED) {
      completedRef.current = true;
      onError?.(
        data.failureReason || "Something went wrong. Please try again.",
        data
      );
    } else if (data.status === targetStatus) {
      completedRef.current = true;
      onComplete?.(data);
    }
  }, [data, targetStatus, onComplete, onError]);

  // Hard wall-clock timeout — react-query has no built-in max-duration, so
  // we maintain one ourselves so the wizard can't get stuck forever. A new
  // runKey is a new episode: re-arm the timeout and the one-shot guard.
  useEffect(() => {
    if (!pollingEnabled) return;
    completedRef.current = false;
    const timerId = setTimeout(() => {
      if (completedRef.current) return;
      completedRef.current = true;
      onError?.("The operation timed out. Please try again.");
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timerId);
  }, [sessionId, runKey, pollingEnabled, onError]);
};
